import { useState, useEffect, useRef } from "react";
import { useSelect, useMultipleSelection } from "downshift";
import { lookupValueById } from "../../common/helpers.js";
import useClickAway from "../../common/useClickAway.js";

import {
  array,
  func,
  string,
  object,
  shape,
  arrayOf,
  node,
  bool,
  any,
} from "prop-types";
import cx from "classnames";
import * as R from "ramda";
import Icon from "../Icon/Icon.jsx";
import "./multi-select.scss";
import { hidden } from "chalk";

const propTypes = {
  activeItems: array,
  placeholder: string.isRequired,
  // onChange shortcut for selectProps.onSelectedItemChange(item.selectedItem)
  // if this is used, you don't need to provide selectProps.onSelectedItemChange
  selectProps: shape({
    items: arrayOf(any).isRequired,
    itemToString: func.isRequired,
    // anything else to go in to useSelect, like onSelectedItemChange or selectedItem
    // see https://github.com/downshift-js/downshift/tree/master/src/hooks/useSelect
    selectedItem: object,
  }).isRequired, // useSelect props
  filterId: string,
  onChange: func,
  className: string,
  selected: object,
  disabled: bool,
  itemToId: func, // can use this instead of itemToString to identify an item
  label: node,
  // placeholder: node,
  renderItem: func, // (item, isSelected) => node
  icon: node,
  handleSubmit: func,
  facetTitle: string,
  selectRange: bool,
};

const MultiSelect = (props) => {
  const {
    selectProps,
    onChange,
    className,
    placeholder,
    label,
    icon,
    selectRange,
    filterId,
    ...otherProps
  } = props;

  const isInputLock = (item, selected) => {
    if (
      R.indexOf(item, selectedItems) !== 0 &&
      R.indexOf(item, selectedItems) !== selectedItems.length - 1 &&
      selectRange
    ) {
      return true;
    } // if less than 2 but more than 0 are selected
    else if (selectedItems.length <= 2 && selectedItems.length > 0) {
      return true;
    }
    return false;
  };

  const { items, itemToString, activeItems, selectedItem } = selectProps;

  const itemToId = props.itemToId || itemToString;

  const [selectedItems, setSelectedItems] = useState([]);
  const [currentFacets, setCurrentFacets] = useState([]);
  const facetMenu = useRef();

  const stateReducer = (state, actionAndChanges) => {
    const { changes, type } = actionAndChanges;
    //
    function keyDown(changes, state) {
      if (state.highlightedIndex < items.length - 1) {
        return {
          ...changes,
          isOpen: true,
          highlightedIndex: state.highlightedIndex + 1,
        };
      } else {
        return {
          ...changes,
        };
      }
    }

    function keyUp(changes, state) {
      if (state.highlightedIndex >= 1) {
        return {
          ...changes,
          isOpen: true,
          highlightedIndex: state.highlightedIndex - 1,
        };
      } else {
        return {
          ...changes,
        };
      }
    }

    switch (
      type //TODO keyup and key down can be combined if we track old and new index and get difference to tell direction
    ) {
      case useSelect.stateChangeTypes.MenuKeyDownArrowDown:
        return keyDown(changes, state);
      case useSelect.stateChangeTypes.MenuKeyDownArrowUp:
        return keyUp(changes, state);
      case useSelect.stateChangeTypes.MenuKeyDownEscape:
        return { ...changes, isOpen: false };
      case useSelect.stateChangeTypes.MenuKeyDownHome:
        return { ...changes, highlightedIndex: 0 };
      case useSelect.stateChangeTypes.MenuKeyDownEnd:
        return {
          ...changes,
          highlightedIndex: items.length - 1,
        };

      case useSelect.stateChangeTypes.MenuKeyDownEnter:
      case useSelect.stateChangeTypes.MenuKeyDownSpaceButton:
      case useSelect.stateChangeTypes.ItemClick:
        // console.log(changes, type, state);
        // if (
        //   isInputLock(item, selectedItems) ||
        //   (selectedItems.length > 4 &&
        //     filterId === "locationid" &&
        //     !selectedItems.includes(item))
        // ) {
        //   return false;
        // }
        return {
          ...changes,
          isOpen: true, // keep menu open after selection.
          highlightedIndex: state.highlightedIndex,
        };
      case useSelect.stateChangeTypes.MenuBlur:
        //need a timeout because the body is focused before next el
        setTimeout(() => {
          if (facetMenu && facetMenu.current !== null) {
            if (!facetMenu.current.contains(document.activeElement)) {
              closeMenu();
            }
          }
        }, 100);
        return {
          ...changes,
          isOpen: true, // keep menu open after selection.
        };
      case useSelect.stateChangeTypes.FunctionCloseMenu:
        if (isOpen) {
          closeMenu();
        }
        return {
          ...changes,
          isOpen: false, // keep menu open after selection.
          highlightedIndex: -1,
        };
      default:
        return changes;
    }
  };

  const { getDropdownProps } = useMultipleSelection({
    initialSelectedItems: currentFacets,
  });

  const selectState = useSelect({
    ...selectProps,
    stateReducer,
    selectedItem: currentFacets,
    defaultHighlightedIndex: 0,
    getA11ySelectionMessage: () => {
      return `${currentFacets.length} item${
        currentFacets.length === 1 ? "s" : ""
      } selected`;
    },

    itemToString: R.prop("value"),
    onIsOpenChange: (event) => {
      const lookup = R.map((item) => lookupValueById("id", item, items))(
        activeItems,
      );
      setSelectedItems(lookup);
      setCurrentFacets(lookup);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return;
      }
      const index = selectedItems.indexOf(selectedItem);
      let selectedArray = [];
      if (index > 0) {
        selectedArray = [
          ...selectedItems.slice(0, index),
          ...selectedItems.slice(index + 1),
        ];
      } else if (index === 0) {
        selectedArray = [...selectedItems.slice(1)];
      } else {
        selectedArray = [...selectedItems, selectedItem];
      }

      setSelectedItems(selectedArray);
      selectProps.onSelectedItemChange &&
        selectProps.onSelectedItemChange(selectedArray);

      onChange && onChange(selectedArray);
    },
  });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    closeMenu,
  } = selectState;
  const renderItem =
    props.renderItem ||
    ((item) => (
      <>
        {itemToString(item)}
        <span className="select__checkmark-border"></span>
      </>
    ));

  let buttonText = `${placeholder} (${selectedItems.length})`;

  // if (selectedItems.length > 2) {
  //   buttonText = `${selectedItems[0].value}, ${selectedItems[1].value} and others selected`;
  // } //
  // else if (selectedItems.length > 1) {
  //   buttonText = `${selectedItems[0].value} and ${selectedItems[1].value} selected`;
  // } //
  // else if (selectedItems.length === 1) {
  //   buttonText = `${selectedItems[0].value} selected`;
  // }

  useClickAway({ isOpen, setOpen: () => closeMenu(), ref: facetMenu });

  useEffect(() => {
    //run everytime active items change
    const lookup = R.map((item) => lookupValueById("id", item, items))(
      activeItems,
    );

    setSelectedItems(lookup);
    setCurrentFacets(lookup);
  }, [activeItems, items]);

  return (
    <div
      className={cx("select", className, {
        "select--open": isOpen,
      })}
      {...otherProps}
      ref={facetMenu}
    >
      <label
        className="select__label"
        {...getLabelProps()}
        style={{
          visibility: hidden,
          height: "1px",
          width: "1px",
          position: "absolute",
        }}
      >
        {/* {label ? label : `Select one or more ${facetTitle}`} */}
      </label>
      <div className="select__dropdown">
        <div className="select__button-holder">
          <button
            className="select__button"
            {...getToggleButtonProps(
              getDropdownProps({ preventKeyAction: isOpen }),
            )}
          >
            <div className="select__button-text" title={buttonText}>
              {icon && <span className="select__button-icon">{icon}</span>}
              {buttonText}
            </div>
          </button>
        </div>
        <ul
          className={cx("select__menu", { "select__menu--range": selectRange })}
          {...getMenuProps()}
        >
          {isOpen &&
            items.map((item, index) => {
              return (
                <li
                  disabled={
                    isInputLock(item, selectedItems) ||
                    (selectedItems.length > 4 &&
                      filterId === "locationid" &&
                      !selectedItems.includes(item))
                      ? "disabled"
                      : ""
                  }
                  className={cx("select__menu-item", {
                    "select__menu-item--selected": selectedItems.includes(item),
                    "select__menu-item--highlighted":
                      highlightedIndex === index,
                    "select__menu-item--disable":
                      selectedItems.length > 4 &&
                      filterId === "locationid" &&
                      !selectedItems.includes(item),
                    "select__menu-item--force-select": isInputLock(
                      item,
                      selectedItems,
                    ),
                  })}
                  // onKeyDown={(e) => {
                  //   console.log(e);
                  //   if (e.target.disabled === true) {
                  //     return false;
                  //   }
                  // }}
                  key={`${item}${index}`}
                  {...getItemProps({
                    item,
                    index,
                  })}
                >
                  {renderItem(item, itemToId(selectedItem) === itemToId(item))}
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
};
MultiSelect.propTypes = propTypes;
export default MultiSelect;
